import { Timeout, dateFormatter, generateBackgroundImgUrl, isValidColor, setStylesOnElement } from "../../util/Misc";
import { setAgentSpeechAvatar } from "./controller/BubbleController";
import { Constants, FOCUS_ON_OPEN } from "../../model/Constants";
import { agentsimpleviewRender } from "../templates/renders/agentsimpleview";
import pubSubInstance from "../../util/pubSubGlobal";

/**
 * Creates the agent-simple-view custom element which will be the container for agent-bubble element,
 * This element handles the margin between bubble elements , also when grouping is enabled all adjacent agent-bubbles are rendered within this element
 * @type {{template, insertBubble(*=): void, created(): void, updateStyleProps(*): void, tagName: string, addBubble(*=, *=): void, render(*=): void, setSpeechImage(*): void}}
 */
const AgentSimpleView: any = {
    tagName: "agent-simple-view",
    initialize(options) {
        initComponent(options);
    },
    renderer: agentsimpleviewRender,
    created() {
        this.container = this.el.querySelector(".bubble-container");
        this.childCount = 0;
        this.agentAliasEl = this.el.querySelector(".msg-info-name span");
        this.timeEl = this.el.querySelector(".msg-info-time span");
        this.msgInfo = this.el.querySelector(".msg-info.below");
        this.lastChid = null;
    },
    getContainer() {
        return this.container;
    },

    /**
     * set the this element styles
     */
    updateStyleProps() {
        setStylesOnElement(this.bubbleStyleProps, this.el.firstChild);
    },

    /**
     * set the styles and also create and render the child agent-bubble element
     * @param {Message} message
     */
    render(message) {
        this.updateStyleProps(message);

        // create agent-bubble element
        this.addBubble(message);

        // set agent alias value in info element, this element will be found only if the agentAlias is configured to display out side agent-bubble element
        if (this.agentAliasEl) {
            this.agentAliasEl.innerText = message.getName();
        }

        // set time value  in info element
        if (this.timeEl) {
            this.timeEl.innerText = dateFormatter(parseInt(message.getTimeStamp(), 10), this.format);
        }

        pubSubInstance.publish(Constants.AGENT_OR_RICH_MEDIA_MESSAGE_RENDERED, this.el.firstElementChild);
    },

    /**
     * Set speech image and position if speech view is enabled
     * @param {Message} message
     */
    setSpeechImage(message) {
        if (this.speechView) {
            setAgentSpeechAvatar.bind(this)(message);
        }
    },

    /**
     * Insert the child agent-bubble as first child in this element
     * @param {Message} message
     */
    insertBubble(message) {
        this.addBubble(message, true);
    },

    /**
     * add the child agent-bubble as last child in this element
     * @param {Message} message
     * @param {boolean} previous
     */
    addBubble(message, previous) {
        let bubble;
        bubble = document.createElement("agent-bubble");

        if (this.childCount === 0) {
            this.setSpeechImage(message);
        }

        /**
         * if not restoring then remove the animation class for info element before rendering child element
         */
        if (!message.getIsRestore()) {
            if (this.msgInfo) {
                this.msgInfo.classList.remove("enter");
            }

            if (this.speechCon) {
                this.speechCon.classList.remove("enter");
            }
        }

        if (bubble) {
            const rd = message.getRichData();

            if (rd == null || rd.widgetView !== "bubble") {
                // doing this allows focus logic to skip and Rich Widget engine can handle the focus
                // Only if the agent bubble is rendering within rich media container, if it is rendering in the previous agent simple view then handle as usuall
                if (rd != null && rd.showMessageText && this.childCount == 0) {
                    bubble.setAttribute(Constants.RM, true);
                }

                bubble.setAttribute("messagetext", message.getMessageText());
            } else {
                bubble.setAttribute(Constants.RM, true);
            }

            bubble.setAttribute("name", message.getName());
            bubble.setAttribute("timestamp", message.getTimeStamp());
            bubble.setAttribute("restore", message.getIsRestore());
            bubble.setAttribute("auto", message.isVA());
            bubble.setAttribute("type", message.getMessageBubble());

            if (previous && this.childCount > 0) {
                this.container.insertBefore(bubble, this.container.firstElementChild);
                bubble.hideBottomInfo();

                if (this.lastChid) {
                    this.lastChid.hideTopInfo();
                }
            } else {
                this.container.appendChild(bubble);

                if (this.childCount > 0) {
                    if (this.lastChid) {
                        this.lastChid.hideBottomInfo();
                    }

                    bubble.hideTopInfo();
                }
            }

            if (rd != null && rd.widgetView === "bubble") {
                const insta = bubble.setRichObject(message.getRichData(), message);
                message.setRichInstance(insta);
            } else if (message.isLast()) {
                if (!rd) {
                    bubble.announceAria();
                }

                this.$app.focusMgr.forceInputFocus();
            }

        }

        /**
         * set the grouped agent-bubble element bottom or top margin
         */
        if (previous && this.childCount > 0) {
            bubble.firstChild.style.marginBottom = `${this.bubbleStyleProps.groupedMargin}px`;
        } else if (this.childCount > 0) {
            bubble.firstChild.style.marginTop = `${this.bubbleStyleProps.groupedMargin}px`;
        }

        /**
         * if not restoring then add the animation class for info element after rendering child element
         */
        if (!message.getIsRestore()) {
            if (Timeout.exists("infotimer") && !Timeout.executed("infotimer")) {
                Timeout.clear("infotimer")();
            }

            Timeout.set("infotimer", () => {
                if (this.speechCon) {
                    this.speechCon.classList.add("enter");
                }

                if (this.msgInfo) {
                    this.msgInfo.classList.add("enter");
                }
            }, 25);
        }

        this.childCount++;
        this.lastChid = bubble;
    },
};

/**
 * @export AgentSimpleView
 * @param {CIApp} appInstance
 * @return {{template, insertBubble, (*=): void, created, (): void, updateStyleProps, (*): void, tagName: string, addBubble, (*=, *=): void, render, (*=): void, setSpeechImage, (*): void}}
 */
export default appInstance => {
    AgentSimpleView.$app = appInstance;
    const constObj: any = {};
    AgentSimpleView.constants = constObj;
    return AgentSimpleView;
};

const initComponent = (options: any) => {
    const appInstance = options.$app;
    const config = appInstance.getUIConfig();
    const constObj: any = options.constants;

    const styleConfig: any = {};
    let bubbleStyleProps;

    if (config.transcriptConfig) {
        const tconfig = config.transcriptConfig;
        bubbleStyleProps = tconfig.bubbleStyleProperties || {};
        constObj.nameStyle = tconfig.agentAliasStyles;
        constObj.timeStyle = tconfig.dateTimeStyles;
        constObj.agentSpeechConfig = tconfig.agentSpeechAvatarConfig;
        constObj.vaSpeechConfig = tconfig.vaSpeechAvatarConfig;
        constObj.openerSpeechConfig = tconfig.openerSpeechAvatarConfig;
        constObj.avatarStyle = tconfig.speechAvatarStyles;
    } else {
        bubbleStyleProps = {};
    }

    styleConfig.marginLeft = `${bubbleStyleProps.agentBubbleLeftMargin || 0}px`;
    styleConfig.marginRight = `${bubbleStyleProps.agentBubbleRightMargin || 100}px`;
    styleConfig.marginBottom = `${bubbleStyleProps.agentBubbleBottomMargin || 15}px`;
    styleConfig.groupedMargin = bubbleStyleProps.groupedBubbleMargin || 5;

    constObj.bubbleStyleProps = styleConfig;

    const appConfig = config.appConfigProps;

    if (appConfig) {
        constObj.showName = appConfig.displayAgentAlias;
        constObj.namePos = appConfig.agentAliasPosition;
        constObj.showTime = appConfig.displayDateAndTime;
        constObj.timePos = appConfig.dateTimePosition;
        constObj.format = appConfig.timeStampFormat || Constants.DEFAULT_TIMESTAMP_FORMAT;
        constObj.speechView = appConfig.setAgentSpeechView;
        constObj.speechPos = appConfig.speechImgPosition;
    }
};
